import { Column } from '@stenajs-webui/core';
import { BannerProps, Card, CardBody } from '@stenajs-webui/elements';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BookingDetailsForm from './components/BookingDetailsForm';
import ClaimInfoDialog from './components/ClaimInfoDialog';
import Modal from './components/Modal';
import Logo from './components/Logo';
import PartyDetailsForm, { PartyDetails } from './components/PartyDetailsForm';
import TapiService from './modules/TapiService';
import UrlResolver from './modules/UrlResolver';
import Loading from './components/Loading';

const tapiService = new TapiService();

function generateServiceUrl(data: { orderNumber: string; jwt: string; customerIndex: string; claim?: any }) {
  const { orderNumber, jwt, customerIndex } = data;

  const serviceJson: Record<string, any> = { order: { id: orderNumber, cust: { id: customerIndex } } };

  if (data.claim) {
    const claim = data.claim;
    serviceJson.claim = {
      id: claim.id
    };
  }

  const serviceParams = encodeURIComponent(JSON.stringify(serviceJson));

  const clientUrl = UrlResolver.getClientUrl();
  const serverUrl = UrlResolver.getBackendUrl(window.location);

  return `${clientUrl}?api=${serverUrl}&tenant=stenaline&service=lynx.claim.register&serviceParams=${serviceParams}&jwt=${jwt}`;
}

export type Order = any;

enum ResponseStatus {
  NotFound = 'not_found',
  Generic = 'generic',
  ClaimTooOld = 'claim_too_old'
}

enum Stages {
  BookingDetails = 'booking_details',
  PartyDetails = 'party_details',
  ClaimExists = 'claim_exists'
}

const responseMap: Record<ResponseStatus, BannerProps> = {
  [ResponseStatus.NotFound]: {
    headerText: 'Booking not found',
    text: 'Please check your input'
  },
  [ResponseStatus.Generic]: {
    headerText: 'Something went wrong',
    text: 'Could not confirm details'
  },
  [ResponseStatus.ClaimTooOld]: {
    headerText: 'Claim too old',
    text: 'The booking already has an old claim that can no longer be changed'
  }
};

const AUTO_REDIRECT_GUARD_KEY = 'lx-claims-auto-redirect-processed';

const App = () => {
  const [order, setOrder] = useState<Order | undefined>();
  const [claim, setClaim] = useState<any | undefined>();
  const [responseStatus, setResponseStatus] = useState<ResponseStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState<Stages>(Stages.BookingDetails);
  const [isInfoOpen, setIsInfoOpen] = useState(false);

  const appRef = useRef<HTMLDivElement | null>(null);

  const openServiceUrl = useCallback((orderNumber: string, jwt: string, customerIndex: string, claim?: any) => {
    window.open(generateServiceUrl({ orderNumber, jwt, customerIndex, claim }), '_self');
  }, []);

  const orderNumber = useMemo(() => order?.identifiers?.find?.((identifier: any) => identifier.domain === 'order')?.identifier ?? '', [order]);
  const customerIndex = useMemo(() => order?.parties?.orderInitiator?.id, [order]);
  const customerName = useMemo(() => order?.parties?.orderInitiator?.name2, [order]);

  const handleSubmit = useCallback(
    (model: PartyDetails) => {
      setLoading(true);
      tapiService
        .signJwt({ orderInitiatorId: customerName, orderNumber, customerIndex, ...model })
        .then((response) => response.json())
        .then(async (body) => {
          setLoading(false);
          const { jwt } = body;
          if (!jwt) {
            setResponseStatus(ResponseStatus.Generic);
            return;
          }

          openServiceUrl(orderNumber, jwt, customerIndex, claim);
        });
    },
    [order, tapiService]
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const bookingReference = searchParams.get('bookingReference');
    const licensePlate = searchParams.get('licensePlate');

    if (!bookingReference || !licensePlate) {
      return;
    }

    const guardKey = `${AUTO_REDIRECT_GUARD_KEY}-${bookingReference}-${licensePlate}`;
    if (window.sessionStorage.getItem(guardKey) === '1') {
      return;
    }

    let cancelled = false;

    const redirectToClaim = async () => {
      setLoading(true);
      setStage(Stages.ClaimExists);

      try {
        const result = await tapiService.getOrder(bookingReference, licensePlate).then((response) => response.json());
        if (cancelled || !result || typeof result !== 'object' || !result.order) {
          setResponseStatus(ResponseStatus.NotFound);
          setStage(Stages.BookingDetails);
          return;
        }

        const { order } = result;
        const orderNumber = order?.identifiers?.find?.((identifier: any) => identifier.domain === 'order')?.identifier ?? '';
        const customerIndex = order?.parties?.orderInitiator?.id;
        const customerName = order?.parties?.orderInitiator?.name2;

        if (!orderNumber || !customerIndex || !customerName) {
          setResponseStatus(ResponseStatus.NotFound);
          setStage(Stages.BookingDetails);
          return;
        }

        const { claim } = await tapiService.getClaim(orderNumber, customerIndex).then((response) => response.json());

        if (cancelled || !claim || typeof claim !== 'object') {
          setResponseStatus(ResponseStatus.Generic);
          setStage(Stages.BookingDetails);
          return;
        }

        const claimInitiator = claim?.parties?.find?.((party: any) => party.role === 'claim_initiator');
        const firstName = claimInitiator?.name;
        const lastName = claimInitiator?.name2;
        const email = claimInitiator?.contactMeans?.find?.((contact: any) => contact.type === 'email')?.value;

        if (!firstName || !lastName || !email) {
          setResponseStatus(ResponseStatus.Generic);
          setStage(Stages.BookingDetails);
          return;
        }

        const { jwt } = await tapiService
          .signJwt({ orderInitiatorId: customerName, orderNumber, customerIndex, firstName, lastName, email })
          .then((response) => response.json());

        if (!jwt) {
          setResponseStatus(ResponseStatus.Generic);
          setStage(Stages.BookingDetails);
          return;
        }

        window.sessionStorage.setItem(guardKey, '1');
        openServiceUrl(orderNumber, jwt, customerIndex, claim);
      } catch {
        if (!cancelled) {
          setResponseStatus(ResponseStatus.Generic);
          setStage(Stages.BookingDetails);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    redirectToClaim();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (claim) {
      const claimInitiator = claim.parties.find((party: any) => party.role === 'claim_initiator');
      const firstName = claimInitiator?.name;
      const lastName = claimInitiator?.name2;
      const email = claimInitiator?.contactMeans?.find((contact: any) => contact.type === 'email')?.value;
      handleSubmit({
        email,
        firstName,
        lastName
      });
    }
  }, [claim]);

  return (
    <div ref={appRef}>
      <Column width={500} position="absolute" top="50%" left="50%" style={{ transform: 'translate(-50%, -50%)' }}>
        <Logo />
        <Card
          style={{
            alignItems: 'center',
            paddingTop: '5em',
            paddingBottom: '13em'
          }}
        >
          <CardBody style={{ width: '65%', textAlign: 'center' }}>
            {
              {
                [Stages.BookingDetails]: (
                  <BookingDetailsForm
                    tapiService={tapiService}
                    onShowInfo={() => setIsInfoOpen(true)}
                    onResponse={async (order) => {
                      if (!order) {
                        setResponseStatus(ResponseStatus.NotFound);
                        return;
                      }

                      setOrder(order);

                      const orderNumber = order?.identifiers?.find?.((identifier: any) => identifier.domain === 'order')?.identifier ?? '';
                      const customerIndex = order?.parties?.orderInitiator?.id;

                      const { claim } = await tapiService.getClaim(orderNumber, customerIndex).then((response) => response.json());

                      if (claim && typeof claim === 'object') {
                        if (claim.isOldClaim) {
                          setResponseStatus(ResponseStatus.ClaimTooOld);
                          return;
                        }

                        setClaim(claim);
                        setStage(Stages.ClaimExists);
                      } else {
                        setStage(Stages.PartyDetails);
                      }
                    }}
                  />
                ),
                [Stages.PartyDetails]: <PartyDetailsForm loading={loading} handleSubmit={handleSubmit} />,
                [Stages.ClaimExists]: <Loading />
              }[stage]
            }
          </CardBody>
        </Card>
        <Modal
          appElement={appRef.current || undefined}
          isOpen={Boolean(responseStatus)}
          onRequestClose={() => setResponseStatus(null)}
          {...(responseStatus ? responseMap[responseStatus] : {})}
        />
        <ClaimInfoDialog appElement={appRef.current || undefined} isOpen={isInfoOpen} onRequestClose={() => setIsInfoOpen(false)} />
      </Column>
    </div>
  );
};

export default App;
